import React from 'react';
import { Movie } from '../types/movie';
import { ExternalLink, Star } from 'lucide-react';

interface MovieCardProps {
  movie: Movie;
  recommendation?: string;
  language: 'zh' | 'en';
}

export default function MovieCard({ movie, recommendation, language }: MovieCardProps) {
  const t = {
    director: language === 'zh' ? '导演' : 'Director',
    actors: language === 'zh' ? '主演' : 'Cast',
    rating: language === 'zh' ? '评分' : 'Rating',
    watchOn: language === 'zh' ? '查看详情' : 'View Details',
    reasonHeader: language === 'zh' ? '为什么推荐这部' : 'Why we recommend this'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group">
      {/* 海报 */}
      <div className="relative h-64 overflow-hidden bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/20">
        <img
          src={movie.poster}
          alt={movie.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {/* 评分徽章 */}
        {movie.rating.douban && (
          <div className="absolute top-3 right-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span className="text-sm font-bold text-gray-800 dark:text-gray-200">
              {movie.rating.douban}
            </span>
          </div>
        )}
      </div>

      {/* 内容 */}
      <div className="p-4">
        {/* 标题 */}
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-1 line-clamp-1">
          {movie.title}
        </h3>
        {movie.titleEn && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            {movie.titleEn}
          </p>
        )}

        {/* 标签 */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {movie.tags.slice(0, 4).map(tag => (
            <span
              key={tag}
              className="px-2 py-0.5 text-xs bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* 信息 */}
        <div className="space-y-1.5 text-sm text-gray-600 dark:text-gray-400 mb-3">
          <div className="flex items-center gap-2">
            <span className="font-medium">{t.director}:</span>
            <span className="truncate">{movie.director}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-medium">{t.actors}:</span>
            <span className="truncate">{movie.actors.join('、')}</span>
          </div>
        </div>

        {/* AI推荐理由 */}
        {recommendation && (
          <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
            <div className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">
              {t.reasonHeader}
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
              {recommendation}
            </p>
          </div>
        )}

        {/* 链接 */}
        <div className="flex gap-2">
          {movie.links.douban && (
            <a
              href={movie.links.douban}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              豆瓣
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {movie.links.imdb && (
            <a
              href={movie.links.imdb}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-white text-sm font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
            >
              IMDb
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
